import { FC, useEffect, useState } from "react";
import SubmitItem from "../../../components/template/SubmitItem/SubmitItem";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, useNavigate } from "react-router-dom";
import { Toast } from "../../../components/atom/Toast/Toast";
import { INDICATOR_URL } from "../../../utils/endpoints";
import { handleSubmitItem } from "../../../helpers/handleSubmitItem";
import IndicatorSubmitForm from "../forms/IndicatorForms/IndicatorSubmitForm";
import {
  IndicatorManagementItemSchema,
  IndicatorCollectionItemSchema,
} from "../data/IndicatorManagementItemSchema";
import IndicatorCollectionForm from "../forms/IndicatorForms/IndicatorCollectionForm";
import { useStepper } from "../../../hooks/stepper/useStepper";
import { StepperConfig } from "../../../types/Components/Stepper/StepperConfig";
import { AlertDialogConfig } from "../../../types/Components/Alert/AlertDialogConfig";
import api from "../../../utils/api";

const NewIndicator: FC = () => {
  const [hasDuplicateNameError, setHasDuplicateNameError] = useState(false);
  const { addToast } = Toast();
  const navigate = useNavigate();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);

  type IndicatorSchemaType =
    | typeof IndicatorManagementItemSchema
    | typeof IndicatorCollectionItemSchema;

  const [currentSchema, setCurrentSchema] = useState<IndicatorSchemaType>(
    IndicatorManagementItemSchema,
  );

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    trigger,
    resetField,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(currentSchema),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  useEffect(() => {
    setValue("healthyLevelOption", null);
    setValue("unhealthyLevelOption", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data: FieldValues) => {
    const reference = (() => {
      const healthySymbolId = data.healthyLevelOption
        ? data.healthyLevelOption.id
        : null;
      const unhealthySymbolId = data.unhealthyLevelOption
        ? data.unhealthyLevelOption.id
        : null;

      if (healthySymbolId === null && unhealthySymbolId === null) {
        return null;
      }

      return {
        healthySymbolId,
        healthyLesserValue:
          healthySymbolId === 2 && data.healthyIntervalTo
            ? data.healthyIntervalTo
            : healthySymbolId === 4 && data.healthyIntervalFrom
              ? data.healthyIntervalFrom
              : null,
        healthyGreaterValue:
          healthySymbolId !== 2 && data.healthyIntervalTo
            ? data.healthyIntervalTo
            : null,
        unhealthySymbolId,
        unhealthyLesserValue:
          unhealthySymbolId === 2 && data.unhealthyIntervalTo
            ? data.unhealthyIntervalTo
            : unhealthySymbolId === 4 && data.unhealthyIntervalFrom
              ? data.unhealthyIntervalFrom
              : null,
        unhealthyGreaterValue:
          unhealthySymbolId !== 2 && data.unhealthyIntervalTo
            ? data.unhealthyIntervalTo
            : null,
      };
    })();

    const bodyRequest = {
      name: data.name,
      typeId: data.indicatorTypeOption.id,
      subSystemId: data.subSystemOption.id,
      frequencyId: data.frequencyOption.id,
      readingVariationId: data.indicatorVariationsOption.id,
      levelId: data.indicatorLevelOption.id,
      alertState: {
        lowestValue: data.belowAbsoluteLevel ? data.belowAbsoluteLevel : null,
        biggestValue: data.aboveAbsoluteLevel ? data.aboveAbsoluteLevel : null,
        considerVariation: data.alertVariationCheck ? true : false,
        maxVariationValue: data.superiorAbsoluteValue
          ? data.superiorAbsoluteValue
          : null,
        minPeriodVariation: data.minPeriodVariation
          ? data.minPeriodVariation
          : null,
        maxPeriodVariation: data.maxPeriodVariation
          ? data.maxPeriodVariation
          : null,
        minYearVariation: data.inferiorYearlyLevelVariations
          ? data.inferiorYearlyLevelVariations
          : null,
        maxYearVariation: data.superiorYearlyLevelVariations
          ? data.superiorYearlyLevelVariations
          : null,
      },
      reference,
      metricId: data.indicatorMetricOption.id,
      anotherMetric: data.outra ? data.outra : "",
      partnerId: data.partnerOption.id,
      indicatorCollectionTypeId: data.integrationOption.id,
    };

    await handleSubmitItem(
      () => api.post(INDICATOR_URL, bodyRequest),
      addToast,
      setIsLoaded,
      "Create",
      "Indicador criado",
    );

    navigate("/gestao-de-indicadores");
  };

  const steps = [
    { title: "Informações do indicador", description: "Passo 1" },
    { title: "Configurar recolha do indicador", description: "Passo 2" },
    { title: "Verificar as informações do indicador", description: "Passo 3" },
  ];

  const { currentStep, handleNext, handleBack, handleFinalSubmit } = useStepper(
    steps,
    trigger,
    handleSubmit,
    onSubmit,
  );

  useEffect(() => {
    const updateSchema = () => {
      currentStep === 1
        ? setCurrentSchema(IndicatorCollectionItemSchema)
        : setCurrentSchema(IndicatorManagementItemSchema);
    };
    updateSchema();
  }, [currentStep]);

  // NewIndicator.tsx

  const handleNextStep = async () => {
    // dispara validações do schema
    const isValid = await trigger();

    // pequena pausa (segurança para atualização de erros assíncronos)
    await new Promise((r) => setTimeout(r, 20));

    // obtém o erro manual atual (caso ainda esteja em formState)
    const nameErrorMessage =
      typeof errors?.name?.message === "string"
        ? errors.name.message.trim()
        : "";

    // considera erro se o estado persistente OU formState contêm erro
    const hasNameError = hasDuplicateNameError || nameErrorMessage.length > 2;

    if (!isValid || hasNameError) {
      // bloqueia avanço
      return;
    }

    handleNext();
  };

  const stepperConfig: StepperConfig = {
    nextButtonName: "Criar Indicador",
    steps,
    onNext:
      currentStep === steps.length - 1 ? handleFinalSubmit : handleNextStep,
    onBack: handleBack,
    isNextActive: true,
    isFinalStep: currentStep === steps.length - 1,
    currentStep,
  };

  const alertDialogConfig: AlertDialogConfig = {
    setIsAlertOpen,
    isAlertOpen,
  };

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <SubmitItem
        title="Criar Indicador"
        hasAlert={currentStep === steps.length - 1}
        hasSubmitFooter
        hasStepper
        maxWidth={"90%"}
        stepperConfig={stepperConfig}
        alertDialogConfig={alertDialogConfig}
        isLoaded={isLoaded}
        isDirty={isDirty}
      >
        {currentStep === 0 && (
          <IndicatorSubmitForm
            errors={errors}
            control={control}
            watch={watch}
            resetField={resetField}
            currentStep={currentStep}
            setError={setError}
            clearErrors={clearErrors}
            setHasDuplicateNameError={setHasDuplicateNameError}
          />
        )}

        {currentStep === 1 && (
          <IndicatorCollectionForm
            errors={errors}
            control={control}
            watch={watch}
          />
        )}

        {currentStep === 2 && (
          <>
            <IndicatorSubmitForm
              errors={errors}
              control={control}
              watch={watch}
              resetField={resetField}
              currentStep={currentStep}
              setError={setError}
              clearErrors={clearErrors}
              setHasDuplicateNameError={setHasDuplicateNameError}
              isDisabled={true}
            />
            <IndicatorCollectionForm
              errors={errors}
              control={control}
              watch={watch}
              isDisabled={true}
            />
          </>
        )}
      </SubmitItem>
    </Form>
  );
};

export default NewIndicator;
